/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: ServerSettingsPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Server Settings module.
----------------------------------------------------------------*/

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit, Server } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useContextStore } from '../../store/context';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import * as api from './api';

export default function ServerSettingsPage() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const { selectedDomain } = useContextStore();

    // Domain Contextual Filtering
    const effectiveDomainId = selectedDomain?.id;

    // Data State
    const [allSettings, setAllSettings] = useState<api.ServerSetting[]>([]);
    const [loading, setLoading] = useState(true);

    // Grid State
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortBy, setSortBy] = useState<string>('name');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set(['name', 'serverDomain', 'sipPort', 'protocol', 'actions']));

    // Modal State
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; settingId: number | null }>({
        isOpen: false,
        settingId: null,
    });

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.getServerSettings(effectiveDomainId);
            setAllSettings(data);
        } catch (error) {
            console.error('Failed to load server settings:', error);
            showError(error instanceof Error ? error.message : 'Failed to load server settings');
        } finally {
            setLoading(false);
        }
    }, [effectiveDomainId, showError]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Derived Data for Grid (Client-side pagination/filtering)
    const processedData = useMemo(() => {
        let filtered = [...allSettings];

        // 1. Search
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(v =>
                v.name.toLowerCase().includes(query) ||
                v.serverDomain.toLowerCase().includes(query)
            );
        }

        // 2. Sort
        filtered.sort((a, b) => {
            let aValue: unknown = a[sortBy as keyof api.ServerSetting];
            let bValue: unknown = b[sortBy as keyof api.ServerSetting];

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        // 3. Pagination
        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);

        return { data: paginatedData, totalItems, totalPages };
    }, [allSettings, searchQuery, sortBy, sortOrder, currentPage, itemsPerPage]);

    // Handlers
    const handleDelete = async () => {
        if (!deleteModal.settingId) return;
        try {
            await api.deleteServerSetting(deleteModal.settingId);
            showSuccess('Server setting deleted successfully');
            setDeleteModal({ isOpen: false, settingId: null });
            refresh();
        } catch (e) {
            showError('Failed to delete server setting');
        }
    };

    // Columns Definition
    const columns = useMemo(() => [
        {
            key: 'name',
            label: 'Name',
            sortable: true,
            visible: true,
            render: (value: string) => <span className="font-medium text-gray-900 dark:text-gray-100">{value}</span>
        },
        {
            key: 'serverDomain',
            label: 'Server Domain',
            sortable: true,
            visible: true,
            render: (value: string) => <span className="font-mono text-sm">{value}</span>
        },
        {
            key: 'sipPort',
            label: 'SIP Port',
            sortable: true,
            visible: true,
        },
        {
            key: 'protocol',
            label: 'Protocol',
            sortable: true,
            visible: true,
            render: (value: string) => (
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium 
                    ${value === 'TLS' ? 'bg-purple-100 text-purple-800' :
                        value === 'TCP' ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'}`}>
                    {value}
                </span>
            )
        },
        {
            key: 'actions',
            label: t('common.actions', 'Actions'),
            visible: true,
            sortable: false,
            align: 'right' as const
        }
    ], [t]);

    const renderActions = useCallback((setting: api.ServerSetting) => (
        <div className="flex items-center justify-end gap-2">
            <SimpleTooltip content={t('common.edit', 'Edit')} position="top">
                <button
                    onClick={() => navigate(`${setting.uuid ?? setting.id}`)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors text-blue-600 dark:text-blue-400"
                >
                    <Edit className="h-4 w-4" />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={t('common.delete', 'Delete')} position="top">
                <button
                    onClick={() => setDeleteModal({ isOpen: true, settingId: setting.id })}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors text-red-600 dark:text-red-400"
                >
                    <Trash2 className="h-4 w-4" />
                </button>
            </SimpleTooltip>
        </div>
    ), [navigate, t]);

    return (
        <div className="p-6">
            <DataGridSimple
                data={processedData.data}
                loading={loading}
                columns={columns}
                pagination={{
                    page: currentPage,
                    totalPages: processedData.totalPages,
                    total: processedData.totalItems,
                    limit: itemsPerPage,
                }}
                translations={{
                    showing: t('common.showing', 'Showing'),
                    to: t('common.to', 'to'),
                    of: t('common.of', 'of'),
                    results: t('common.results', 'results'),
                    items: t('common.items', 'Items'),
                    columns: t('common.columns', 'Columns'),
                    noItemsFound: t('common.noData', 'No items found'),
                    allSelected: t('common.allSelected', 'All Selected'),
                    selected: t('common.selected', 'Selected'),
                    searchOptions: t('common.search', 'Search'),
                    noOptionsFound: t('common.noResults', 'No results'),
                    selectAll: t('common.selectAll', 'Select All'),
                    clearAll: t('common.clearAll', 'Clear All'),
                    previous: t('common.previous', 'Previous'),
                    next: t('common.next', 'Next'),
                    page: t('common.page', 'Page'),
                    deleteItem: t('common.deleteItem', 'Delete Item'),
                    areYouSureDeleteItem: t('common.confirmDelete', 'Are you sure you want to delete this item?'),
                    delete: t('common.delete', 'Delete'),
                    cancel: t('common.cancel', 'Cancel'),
                }}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSearch={setSearchQuery}
                onSort={(column) => {
                    if (sortBy === column) {
                        setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                    } else {
                        setSortBy(column);
                        setSortOrder('asc');
                    }
                }}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
                onColumnToggle={(column) => {
                    const newSet = new Set(visibleColumns);
                    if (newSet.has(column)) newSet.delete(column);
                    else newSet.add(column);
                    setVisibleColumns(newSet);
                }}
                renderActions={renderActions}
                searchPlaceholder="Search settings..."
                title="Server Settings"
                titleIcon={<Server className="h-5 w-5 text-primary" />}
                addButtonText={t('common.add', 'Add')}
                onAdd={() => navigate('new')}
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, settingId: null })}
                onConfirm={handleDelete}
                title={t('common.deleteItem', 'Delete Setting')}
                message={t('common.confirmDeleteMessage', 'Are you sure you want to delete this setting?')}
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />
        </div>
    );
}
